import { useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Badge, type BadgeVariant } from '@heyroot/ui/primitives/Badge';
import { Button } from '@heyroot/ui/primitives/Button';
import { EmptyState } from '@heyroot/ui/primitives/EmptyState';
import { Input } from '@heyroot/ui/primitives/Input';
import { SkeletonTable } from '@heyroot/ui/primitives/Skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@heyroot/ui/primitives/Table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@heyroot/backoffice-ui';
import { fetchEscalations, queryKeys, type ListEscalationsParams } from '../lib/api';

const PAGE_SIZE = 25;

// "All statuses" is a real, re-selectable filter state — Radix SelectItem can
// never carry value="" (check-radix-empty-select), so it needs a sentinel
// mapped back to '' at the statusFilter boundary.
const ALL_STATUSES_SENTINEL = '__all__';

function tierVariant(tier: string | null): BadgeVariant {
  if (tier === 'high') return 'success';
  if (tier === 'medium') return 'warning';
  if (tier === 'low') return 'danger';
  return 'neutral';
}

function statusVariant(s: string): BadgeVariant {
  return s === 'open' ? 'warning' : 'neutral';
}

export function AssistantInboxPage() {
  const navigate = useNavigate();
  const [tenantId, setTenantId] = useState('');
  const [statusFilter, setStatusFilter] = useState<'open' | 'closed' | ''>('');
  const [offset, setOffset] = useState(0);

  const params: ListEscalationsParams = {
    limit: PAGE_SIZE,
    offset,
    ...(tenantId ? { tenantId } : {}),
    ...(statusFilter ? { status: statusFilter } : {}),
  };

  const { data, isPending, isError, error } = useQuery({
    queryKey: queryKeys.escalations(params),
    queryFn: () => fetchEscalations(params),
    placeholderData: keepPreviousData,
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const page = Math.floor(offset / PAGE_SIZE);

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-heading-2 text-primary">Assistant Escalations</h1>
        <p className="mt-1 text-body-sm text-muted">
          {total > 0
            ? `${total} escalations`
            : 'Cross-tenant view of assistant support escalations.'}
        </p>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2.5 flex-wrap">
        <div className="flex-1 basis-60 max-w-80">
          <Input
            type="search"
            placeholder="Filter by tenant ID…"
            value={tenantId}
            onChange={(e) => {
              setTenantId(e.target.value);
              setOffset(0);
            }}
            aria-label="Filter by tenant ID"
          />
        </div>
        <Select
          value={statusFilter || ALL_STATUSES_SENTINEL}
          onValueChange={(v) => {
            setStatusFilter(v === ALL_STATUSES_SENTINEL ? '' : (v as 'open' | 'closed'));
            setOffset(0);
          }}
        >
          <SelectTrigger aria-label="Filter by status" className="h-[1.875rem]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_STATUSES_SENTINEL}>All statuses</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isError && !isPending && (
        <div
          role="alert"
          className="rounded-control border border-danger bg-danger-pale text-danger-text text-body-sm p-4"
        >
          {error instanceof Error ? error.message : 'Failed to load escalations.'}
        </div>
      )}

      {!isPending && !isError && rows.length === 0 && (
        <EmptyState
          title="No escalations found"
          description={
            tenantId || statusFilter
              ? 'No results for these filters.'
              : 'Escalations appear here when operators request support.'
          }
        />
      )}

      {(isPending || (!isError && rows.length > 0)) && (
        <>
          <div className="overflow-x-auto">
            <Table dense>
              <TableHead>
                <tr>
                  <TableHeaderCell>Tenant</TableHeaderCell>
                  <TableHeaderCell>Question</TableHeaderCell>
                  <TableHeaderCell>Confidence</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                  <TableHeaderCell>Created</TableHeaderCell>
                </tr>
              </TableHead>
              <TableBody>
                {isPending ? (
                  <SkeletonTable rows={6} cols={5} />
                ) : (
                  rows.map((row) => (
                    <TableRow
                      key={row.id}
                      className="cursor-pointer"
                      onClick={() =>
                        void navigate({
                          to: '/assistant/$escalationId',
                          params: { escalationId: row.id },
                        })
                      }
                    >
                      <TableCell>
                        <span className="font-mono text-caption text-muted">
                          {row.tenantId.slice(0, 8)}…
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="line-clamp-2 text-body-sm text-primary">
                          {row.question}
                        </span>
                      </TableCell>
                      <TableCell>
                        {row.confidenceTier ? (
                          <Badge variant={tierVariant(row.confidenceTier)}>
                            {row.confidenceTier}
                          </Badge>
                        ) : (
                          <span className="text-muted text-caption">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={statusVariant(row.status)}>{row.status}</Badge>
                      </TableCell>
                      <TableCell className="text-caption text-muted">
                        {new Date(row.createdAt).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {!isPending && totalPages > 1 && (
            <div className="flex items-center justify-end gap-2 pt-2">
              <span className="text-body-sm text-muted">
                Page {page + 1} of {totalPages}
              </span>
              <Button
                size="sm"
                variant="secondary"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={offset + PAGE_SIZE >= total}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
